/**
 * VEHICLES round (2026-10-01, Matt: "I need a few cars to be able to put in driveways and
 * garages. A sedan and an SUV would be good. Make them look nice."): procedural cars for
 * site and garage planning — Revit's Entourage. Local frame (the object convention):
 * x across the width centered on 0, y from the REAR bumper (y = 0, the anchor) to the
 * front, z up from the ground.
 *
 * One SIDE PROFILE per body part (convex polygons in y–z), extruded across a width, so
 * the hood, cowl, glasshouse and decklid read as a car rather than stacked boxes: a
 * lower body with its bumper chamfers and wheel arches, a tinted GLASS cabin (the
 * proto's translucent channel) under a solid roof with A/B/C pillars, four tires with
 * wheel faces just proud of the body side, and mirrors. Both kinds share one builder;
 * the per-kind numbers live in VEHICLE_SHAPES.
 */
import type { MeshBuilder } from '../../extrude'
import type { Pt2 } from '@aj/bim-kernel'

export interface VehicleShape {
  /** ride height — the body's underside */
  clearance: number
  /** wheel radius and the axle stations as fractions of the length */
  wheelR: number
  axleRear: number
  axleFront: number
  /** belt line (where the glass starts) as a fraction of the height */
  belt: number
  /** the hood's front edge height as a fraction of the height */
  noseZ: number
  /** windshield base (cowl) and roof-front stations, fractions of the length from the rear */
  cowl: number
  roofFront: number
  /** roof-rear and back-glass base stations */
  roofRear: number
  backBase: number
  /** decklid / tailgate top height as a fraction of the height (sedan trunk) */
  deckZ: number
  roofRails: boolean
}

export const VEHICLE_SHAPES: Record<string, VehicleShape> = {
  carSedan: {
    clearance: 0.45, wheelR: 1.12, axleRear: 0.19, axleFront: 0.79, belt: 0.56, noseZ: 0.43,
    cowl: 0.66, roofFront: 0.53, roofRear: 0.27, backBase: 0.16, deckZ: 0.57, roofRails: false,
  },
  carSUV: {
    clearance: 0.7, wheelR: 1.3, axleRear: 0.18, axleFront: 0.8, belt: 0.58, noseZ: 0.55,
    cowl: 0.7, roofFront: 0.6, roofRear: 0.06, backBase: 0.03, deckZ: 0.58, roofRails: true,
  },
}

/** extrude a convex CCW (y, z) polygon across x0..x1 into `mb` */
function extrudeX(mb: MeshBuilder, poly: Pt2[], x0: number, x1: number) {
  const n = poly.length
  if (n < 3 || x1 - x0 < 1e-4) return
  const L = poly.map(([y, z]) => [x0, y, z]), R = poly.map(([y, z]) => [x1, y, z])
  for (let i = 1; i < n - 1; i++) {
    mb.addTri(L[0], L[i + 1], L[i], [-1, 0, 0])
    mb.addTri(R[0], R[i], R[i + 1], [1, 0, 0])
  }
  for (let i = 0; i < n; i++) {
    const j = (i + 1) % n
    const dy = poly[j][0] - poly[i][0], dz = poly[j][1] - poly[i][1]
    const len = Math.hypot(dy, dz) || 1
    const nn = [0, dz / len, -dy / len]          // outward for a CCW (y, z) loop
    mb.addTri(L[i], R[i], R[j], nn)
    mb.addTri(L[i], R[j], L[j], nn)
  }
}

/** a cylinder along x (a tire): center (y, z), radius r, from x0 to x1 */
function cylX(mb: MeshBuilder, y: number, z: number, r: number, x0: number, x1: number, segs = 20) {
  const pt = (x: number, a: number) => [x, y + Math.cos(a) * r, z + Math.sin(a) * r]
  for (let i = 0; i < segs; i++) {
    const a0 = (Math.PI * 2 * i) / segs, a1 = (Math.PI * 2 * (i + 1)) / segs, am = (a0 + a1) / 2
    const nn = [0, Math.cos(am), Math.sin(am)]
    mb.addTri(pt(x0, a0), pt(x1, a0), pt(x1, a1), nn)
    mb.addTri(pt(x0, a0), pt(x1, a1), pt(x0, a1), nn)
    mb.addTri([x1, y, z], pt(x1, a0), pt(x1, a1), [1, 0, 0])
    mb.addTri([x0, y, z], pt(x0, a1), pt(x0, a0), [-1, 0, 0])
  }
}

function boxM(mb: MeshBuilder, x0: number, x1: number, y0: number, y1: number, z0: number, z1: number) {
  extrudeX(mb, [[y0, z0], [y1, z0], [y1, z1], [y0, z1]], x0, x1)
}

/** build one vehicle: the body into `solid`, the glasshouse into `glass` */
export function buildVehicle(kind: string, w: number, d: number, h: number, solid: MeshBuilder, glass: MeshBuilder): void {
  const s = VEHICLE_SHAPES[kind] || VEHICLE_SHAPES.carSedan
  const zb = s.clearance
  const zBelt = h * s.belt, zNose = h * s.noseZ, zDeck = h * s.deckZ, zRoof = h
  const yCowl = d * s.cowl, yRoofF = d * s.roofFront, yRoofR = d * s.roofRear, yBack = d * s.backBase
  const yAxR = d * s.axleRear, yAxF = d * s.axleFront, r = s.wheelR

  // ---- LOFTED BODY: rounded cross-sections along the length, joined into one skin.
  // Each station's ring is a rounded rectangle in x–z whose half-width, top and bottom
  // follow the car's lines: the tapered nose and tail in plan, the hood falling to the
  // nose and the decklid to the tail, and — over each wheel — the bottom lifted along a
  // true circular ARCH round the tire, so the wells read as wells.
  const hw = w / 2
  const arch = r + 0.14
  const yNose = d
  const smooth = (t: number) => t * t * (3 - 2 * t)
  const topZ = (y: number): number => {
    if (y >= yCowl) {
      const t = Math.min(1, (y - yCowl) / Math.max(0.01, yNose - yCowl))
      return zBelt + (zNose - zBelt) * t - 0.25 * Math.pow(Math.max(0, (y - (d - 0.6)) / 0.6), 2)
    }
    if (y >= yBack) return zBelt
    const t = 1 - y / Math.max(0.01, yBack)
    return zDeck - 0.15 * t - 0.25 * Math.pow(Math.max(0, (0.5 - y) / 0.5), 2)
  }
  const botZ = (y: number): number => {
    let z = zb
    if (y < 0.7) z += 0.45 * Math.pow((0.7 - y) / 0.7, 2)           // rear bumper tuck
    if (y > d - 0.8) z += 0.5 * Math.pow((y - (d - 0.8)) / 0.8, 2)   // front air dam tuck
    for (const ya of [yAxR, yAxF]) {
      const dy = y - ya
      if (Math.abs(dy) < arch) z = Math.max(z, r + Math.sqrt(arch * arch - dy * dy))
    }
    return z
  }
  const halfW = (y: number): number => {
    const tf = Math.max(0, (y - (d - 2.2)) / 2.2), tr = Math.max(0, (1.6 - y) / 1.6)
    return hw * (1 - 0.1 * smooth(tf) * smooth(tf) - 0.06 * smooth(tr) * smooth(tr))
  }
  const N_SIDE = 3, N_ARC = 4
  /** one ring: CCW in x–z seen from +y (bottom, right side, right shoulder, top, left
   *  shoulder, left side) — the SAME point count at every station */
  const ring = (y: number, hwY: number, zBot: number, zTop: number, rs: number): number[][] => {
    const pts: number[][] = []
    const rad = Math.max(0.02, Math.min(rs, (zTop - zBot) * 0.45, hwY * 0.45))
    const rb = Math.min(0.12, (zTop - zBot) * 0.2)
    pts.push([-hwY + rb, y, zBot], [hwY - rb, y, zBot])
    for (let i = 0; i <= N_SIDE; i++) pts.push([hwY, y, zBot + rb + (zTop - rad - zBot - rb) * (i / N_SIDE)])
    for (let i = 1; i <= N_ARC; i++) {
      const a = (Math.PI / 2) * (i / N_ARC)
      pts.push([hwY - rad + Math.cos(a) * rad, y, zTop - rad + Math.sin(a) * rad])
    }
    for (let i = 1; i <= N_ARC; i++) {
      const a = Math.PI / 2 + (Math.PI / 2) * (i / N_ARC)
      pts.push([-hwY + rad + Math.cos(a) * rad, y, zTop - rad + Math.sin(a) * rad])
    }
    for (let i = 0; i <= N_SIDE; i++) pts.push([-hwY, y, zTop - rad - (zTop - rad - zBot - rb) * (i / N_SIDE)])
    return pts
  }
  const tri = (mb: MeshBuilder, a: number[], b2: number[], c: number[]) => {
    const ux = b2[0] - a[0], uy = b2[1] - a[1], uz = b2[2] - a[2]
    const vx = c[0] - a[0], vy = c[1] - a[1], vz = c[2] - a[2]
    const nx = uy * vz - uz * vy, ny = uz * vx - ux * vz, nz = ux * vy - uy * vx
    const L = Math.hypot(nx, ny, nz)
    if (L < 1e-12) return
    mb.addTri(a, b2, c, [nx / L, ny / L, nz / L])
  }
  /** skin consecutive rings (stations ascending in y) and cap both ends */
  const loft = (mb: MeshBuilder, rings: number[][][]) => {
    for (let k = 0; k + 1 < rings.length; k++) {
      const A = rings[k], B = rings[k + 1], n = A.length
      for (let i = 0; i < n; i++) {
        const j = (i + 1) % n
        tri(mb, A[i], A[j], B[j])
        tri(mb, A[i], B[j], B[i])
      }
    }
    const first = rings[0], last = rings[rings.length - 1]
    for (let i = 1; i + 1 < first.length; i++) tri(mb, first[0], first[i + 1], first[i])
    for (let i = 1; i + 1 < last.length; i++) tri(mb, last[0], last[i], last[i + 1])
  }
  // stations: dense enough for the arches and the hood curve, exact at the key lines
  const ys = new Set<number>()
  for (let y = 0; y <= d + 1e-9; y += 0.25) ys.add(+y.toFixed(4))
  for (const ya of [yAxR, yAxF]) for (let k = -6; k <= 6; k++) ys.add(+(ya + (arch * k) / 6).toFixed(4))
  for (const y of [yCowl, yBack, d]) ys.add(+y.toFixed(4))
  const bodyYs = [...ys].filter((y) => y >= 0 && y <= d).sort((a, b) => a - b)
  loft(solid, bodyYs.map((y) => ring(y, halfW(y), botZ(y), topZ(y), 0.5)))

  // ---- GLASSHOUSE: a lofted cabin with tumblehome (narrower at the roof), tinted glass
  // from the belt to just under the roof skin; the windshield and back glass are its
  // sloped ends
  const gw = hw - Math.max(0.2, w * 0.035)
  const tumble = Math.min(0.55, w * 0.08)
  const zG = (y: number): number => {
    if (y <= yRoofR) return zBelt + (zRoof - zBelt) * Math.max(0, (y - yBack) / Math.max(0.01, yRoofR - yBack))
    if (y >= yRoofF) return zBelt + (zRoof - zBelt) * Math.max(0, (yCowl - y) / Math.max(0.01, yCowl - yRoofF))
    return zRoof
  }
  const cabRing = (y: number, zTop: number, inset: number): number[][] => {
    const z0 = zBelt - 0.05, ht = Math.max(0.001, zTop - z0)
    const b = gw - inset, t = gw - tumble - inset
    const top = Math.max(0.05, t)
    return ring(y, b, z0, z0 + ht, 0.25).map((q) => {
      // lean the sides in toward the roof: scale x by height up the ring
      const f = (q[2] - z0) / ht
      const hwAt = b + (top - b) * f
      return [Math.sign(q[0]) * Math.min(Math.abs(q[0]), hwAt), q[1], q[2]]
    })
  }
  const cabYs: number[] = []
  for (let k = 0; k <= 24; k++) cabYs.push(yBack + ((yCowl - yBack) * k) / 24)
  for (const y of [yRoofR, yRoofF]) cabYs.push(y)
  cabYs.sort((a, b) => a - b)
  loft(glass, cabYs.map((y) => cabRing(y, Math.max(zBelt + 0.02, zG(y) - 0.09), 0.03)))
  // roof skin: the cabin's top shell over the flat roof stretch, body color
  const roofYs = cabYs.filter((y) => y >= yRoofR - 0.3 && y <= yRoofF + 0.3)
  const roofHalf = Math.max(0.05, gw - tumble) + 0.03
  loft(solid, roofYs.map((y) => ring(y, roofHalf, zG(y) - 0.13, zG(y) + 0.01, 0.12)))

  // pillars: A along the windshield edge, B mid-cabin, C along the back glass — slim
  // body-colour strips on the glass, leaning in with the tumblehome
  const xp = gw - tumble * 0.5
  const pillar = (ya0: number, za0: number, ya1: number, za1: number, t: number) => {
    for (const sx of [-1, 1]) {
      const x0 = sx < 0 ? -xp - 0.06 : xp - 0.08, x1 = sx < 0 ? -xp + 0.08 : xp + 0.06
      extrudeX(solid, [[ya0 - t, za0], [ya0 + t, za0], [ya1 + t, za1], [ya1 - t, za1]], x0, x1)
    }
  }
  pillar(yCowl, zBelt, yRoofF, zRoof - 0.08, 0.1)
  const yB = yRoofR + (yRoofF - yRoofR) * 0.5
  for (const sx of [-1, 1]) {
    const x0 = sx < 0 ? -xp - 0.05 : xp - 0.08, x1 = sx < 0 ? -xp + 0.08 : xp + 0.05
    boxM(solid, x0, x1, yB - 0.16, yB + 0.16, zBelt, zRoof - 0.08)
  }
  pillar(yBack, zBelt, yRoofR, zRoof - 0.08, s.roofRails ? 0.32 : 0.24)
  if (s.roofRails) {
    for (const sx of [-1, 1]) {
      const xr = sx * (gw - tumble - 0.2)
      boxM(solid, xr - 0.05, xr + 0.05, yRoofR + 0.5, yRoofF - 0.4, zRoof - 0.02, zRoof + 0.16)
    }
  }
  // door mirrors at the base of the A-pillar
  for (const sx of [-1, 1]) {
    const x0 = sx < 0 ? -hw - 0.42 : hw - 0.05, x1 = sx < 0 ? -hw + 0.05 : hw + 0.42
    boxM(solid, x0, x1, yCowl - 0.6, yCowl - 0.2, zBelt + 0.1, zBelt + 0.45)
  }

  // ---- WHEELS: the tire, and a recessed rim face with a hub so it reads as a wheel
  for (const ya of [yAxR, yAxF]) {
    for (const sx of [-1, 1]) {
      const out = sx * (halfW(ya) - 0.02), inn = sx * (halfW(ya) - 0.75)
      cylX(solid, ya, r, r, Math.min(out, inn), Math.max(out, inn))
      const rimFace = sx * (halfW(ya) + 0.0), rimIn = sx * (halfW(ya) - 0.06)
      cylX(solid, ya, r, r * 0.62, Math.min(rimFace, rimIn), Math.max(rimFace, rimIn), 18)
      const hubO = sx * (halfW(ya) + 0.03)
      cylX(solid, ya, r, r * 0.16, Math.min(hubO, rimIn), Math.max(hubO, rimIn), 10)
    }
  }
}

/** the plan symbol (top view) in the same local frame: body, glass lines, roof, mirrors */
export function vehiclePlanSymbol(kind: string, w: number, d: number): { pts: Pt2[]; closed?: boolean }[] {
  const s = VEHICLE_SHAPES[kind] || VEHICLE_SHAPES.carSedan
  const hw = w / 2
  const out: { pts: Pt2[]; closed?: boolean }[] = []
  // body: rounded at all four corners, the nose a little fuller than the tail
  const rr = (cx: number, cy: number, r: number, a0: number): Pt2[] =>
    Array.from({ length: 5 }, (_, i) => {
      const a = a0 + (Math.PI / 2) * (i / 4)
      return [cx + Math.cos(a) * r, cy + Math.sin(a) * r] as Pt2
    })
  const rf = Math.min(1.1, hw * 0.4), rb = Math.min(0.7, hw * 0.3)
  out.push({
    closed: true,
    pts: [
      ...rr(hw - rf, d - rf, rf, 0), ...rr(-hw + rf, d - rf, rf, Math.PI / 2),
      ...rr(-hw + rb, rb, rb, Math.PI), ...rr(hw - rb, rb, rb, -Math.PI / 2),
    ],
  })
  const gw = hw - Math.max(0.22, w * 0.04)
  const yCowl = d * s.cowl, yRoofF = d * s.roofFront, yRoofR = d * s.roofRear, yBack = d * s.backBase
  const bow = (y: number, k: number): Pt2[] => [[-gw, y], [-gw * 0.5, y + k], [0, y + k * 1.3], [gw * 0.5, y + k], [gw, y]]
  out.push({ pts: bow(yCowl, 0.25) })                       // windshield base (cowl)
  out.push({ pts: bow(yRoofF, 0.15) })                      // windshield top / roof front
  out.push({ pts: [[-gw, yRoofF], [-gw, yRoofR], [gw, yRoofR], [gw, yRoofF]] })   // roof sides
  out.push({ pts: bow(yRoofR, -0.12) })                     // roof rear
  out.push({ pts: bow(yBack, -0.2) })                       // back glass base
  out.push({ pts: [[-gw, yCowl], [-gw, yBack]] })           // the glass line down each side
  out.push({ pts: [[gw, yCowl], [gw, yBack]] })
  for (const sx of [-1, 1]) {                               // mirrors
    const x0 = sx < 0 ? -hw - 0.45 : hw, x1 = sx < 0 ? -hw : hw + 0.45
    out.push({ closed: true, pts: [[x0, yCowl - 0.55], [x1, yCowl - 0.55], [x1, yCowl - 0.15], [x0, yCowl - 0.15]] })
  }
  if (s.roofRails) for (const sx of [-1, 1]) out.push({ pts: [[sx * (gw - 0.25), yRoofR + 0.4], [sx * (gw - 0.25), yRoofF - 0.3]] })
  return out
}
